import { useState, useEffect } from "react";
import { fetchAdminUsers } from "../services/apiService";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export default function AdminUsersView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    fetchAdminUsers().then(data => {
      if (data === "forbidden") {
        setForbidden(true);
      } else if (data) {
        setUsers(data);
      }
      setLoading(false);
    });
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
        Đang tải...
      </div>
    );
  }

  if (forbidden) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "48px", marginBottom: "12px" }}>🔒</div>
        <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>Không có quyền truy cập</div>
        <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "8px" }}>Trang này chỉ dành cho admin.</div>
      </div>
    );
  }

  // Filter by search
  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q);
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    switch (sortBy) {
      case "created_at":
        va = new Date(a.created_at || 0).getTime();
        vb = new Date(b.created_at || 0).getTime();
        break;
      case "last_sign_in_at":
        va = new Date(a.last_sign_in_at || 0).getTime();
        vb = new Date(b.last_sign_in_at || 0).getTime();
        break;
      case "streak":
        va = a.streak?.current_streak || 0;
        vb = b.streak?.current_streak || 0;
        break;
      case "longest_streak":
        va = a.streak?.longest_streak || 0;
        vb = b.streak?.longest_streak || 0;
        break;
      case "challenges":
        va = a.challenges?.completed || 0;
        vb = b.challenges?.completed || 0;
        break;
      case "reflections":
        va = a.reflections || 0;
        vb = b.reflections || 0;
        break;
      default:
        va = a.name || "";
        vb = b.name || "";
    }
    if (sortDir === "asc") return va > vb ? 1 : -1;
    return va < vb ? 1 : -1;
  });

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => {
    const days = daysSince(u.last_sign_in_at);
    return days !== null && days <= 7;
  }).length;
  const withStreak = users.filter(u => (u.streak?.current_streak || 0) > 0).length;
  const totalCompleted = users.reduce((sum, u) => sum + (u.challenges?.completed || 0), 0);

  const SortBtn = ({ field, label }) => (
    <button onClick={() => handleSort(field)} style={{
      background: sortBy === field ? "#3B82F615" : "transparent",
      border: `1px solid ${sortBy === field ? "#3B82F644" : "var(--border-primary)"}`,
      borderRadius: "8px", padding: "4px 10px", cursor: "pointer",
      fontSize: "10px", fontWeight: "600",
      color: sortBy === field ? "#3B82F6" : "var(--text-muted)",
    }}>
      {label} {sortBy === field ? (sortDir === "desc" ? "↓" : "↑") : ""}
    </button>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <div style={{ fontSize: "11px", letterSpacing: "2px", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "4px" }}>Admin</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: "var(--text-primary)" }}>Quản lý người dùng</div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", overflowX: "auto" }}>
        <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "70px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "var(--text-primary)" }}>{totalUsers}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Tổng users</div>
        </div>
        <div style={{ flex: 1, background: "#10B98115", border: "1px solid #10B98144", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "70px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#10B981" }}>{activeUsers}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Active (7 ngày)</div>
        </div>
        <div style={{ flex: 1, background: "#F59E0B15", border: "1px solid #F59E0B44", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "70px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#F59E0B" }}>{withStreak}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Có streak</div>
        </div>
        <div style={{ flex: 1, background: "#3B82F615", border: "1px solid #3B82F644", borderRadius: "12px", padding: "12px", textAlign: "center", minWidth: "70px" }}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#3B82F6" }}>{totalCompleted}</div>
          <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Challenges done</div>
        </div>
      </div>

      {/* Search */}
      <input
        type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Tìm theo tên hoặc email..."
        style={{
          width: "100%", padding: "10px 14px", fontSize: "13px",
          background: "var(--bg-card)", border: "1px solid var(--border-primary)",
          borderRadius: "10px", color: "var(--text-primary)", outline: "none",
          marginBottom: "12px", boxSizing: "border-box",
        }}
      />

      {/* Sort buttons */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: "10px", color: "var(--text-muted)", marginRight: "4px" }}>Sắp xếp:</span>
        <SortBtn field="created_at" label="Ngày tạo" />
        <SortBtn field="last_sign_in_at" label="Login gần nhất" />
        <SortBtn field="streak" label="Streak" />
        <SortBtn field="longest_streak" label="Kỷ lục" />
        <SortBtn field="challenges" label="Challenges" />
        <SortBtn field="reflections" label="Reflections" />
      </div>

      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "10px" }}>
        {filtered.length}/{users.length} người dùng
      </div>

      {/* User list */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {sorted.map(u => {
          const daysAgo = daysSince(u.last_sign_in_at);
          const isActive = daysAgo !== null && daysAgo <= 7;
          const isNew = daysSince(u.created_at) <= 7;

          return (
            <div key={u.id} style={{
              background: "var(--bg-card)",
              border: `1px solid ${isNew ? "#10B98144" : "var(--border-secondary)"}`,
              borderRadius: "14px", padding: "16px",
            }}>
              {/* User info row */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                {u.avatar ? (
                  <img src={u.avatar} alt="" style={{ width: 40, height: 40, borderRadius: "50%", border: "2px solid var(--border-secondary)" }} />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%", background: "#7C3AED",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "16px", color: "#fff", fontWeight: "700",
                    border: "2px solid var(--border-secondary)",
                  }}>
                    {(u.name || u.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "var(--text-primary)" }}>
                      {u.name}
                    </span>
                    {isNew && (
                      <span style={{ fontSize: "9px", background: "#10B98120", color: "#10B981", padding: "2px 6px", borderRadius: "4px", fontWeight: "600" }}>MỚI</span>
                    )}
                    {isActive && (
                      <span style={{ fontSize: "9px", background: "#3B82F620", color: "#3B82F6", padding: "2px 6px", borderRadius: "4px", fontWeight: "600" }}>ACTIVE</span>
                    )}
                  </div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.email}
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                <div style={{ background: "var(--bg-main)", borderRadius: "8px", padding: "8px", textAlign: "center" }}>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#F59E0B" }}>
                    🔥 {u.streak?.current_streak || 0}
                  </div>
                  <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Streak</div>
                </div>
                <div style={{ background: "var(--bg-main)", borderRadius: "8px", padding: "8px", textAlign: "center" }}>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#EF4444" }}>
                    {u.streak?.longest_streak || 0}
                  </div>
                  <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Kỷ lục</div>
                </div>
                <div style={{ background: "var(--bg-main)", borderRadius: "8px", padding: "8px", textAlign: "center" }}>
                  <div style={{ fontSize: "16px", fontWeight: "800", color: "#10B981" }}>
                    {u.challenges?.completed || 0}/{u.challenges?.total || 0}
                  </div>
                  <div style={{ fontSize: "9px", color: "var(--text-muted)" }}>Challenges</div>
                </div>
              </div>

              {/* Footer info */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px", flexWrap: "wrap", gap: "6px" }}>
                <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                  Tham gia: {formatDate(u.created_at)}
                </div>
                <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                  Login gần nhất: {formatDateTime(u.last_sign_in_at)}
                </div>
                <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                  Reflections: {u.reflections}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text-muted)", fontSize: "13px" }}>
          {users.length === 0 ? "Chưa có người dùng nào." : "Không tìm thấy người dùng."}
        </div>
      )}
    </div>
  );
}
